import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { LoadingService } from '@core/services/loading.service';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-add-articles',
  templateUrl: './add-articles.component.html',
  styleUrls: ['./add-articles.component.scss']
})
export class AddArticlesComponent implements OnInit {
  formData: any = {
    fake_user_id: '',
    title: '',
    body: '',
    image: null,
    images: []
  };
  fakeUsers: any[] = [];
  imagePreview: string | ArrayBuffer | null = null;
  extraImagePreviews: string[] = [];
  isSubmitting = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apisService: ApisService,
    private loading: LoadingService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadFormData();
  }

  loadFormData(): void {
    this.loading.show();
    const preselectedUserId = this.route.snapshot.queryParamMap.get('fakeUserId');

    this.apisService.getArticleFormData().subscribe({
      next: (res) => {
        this.fakeUsers = res?.data?.fake_users || [];
        if (preselectedUserId) {
          this.formData.fake_user_id = preselectedUserId;
        }
        this.loading.hide();
      },
      error: () => {
        this.loading.hide();
        this.showAlert('MENU.DASHBOARD.ERROR', 'MENU.DASHBOARD.UNEXPECTED_ERROR', 'error');
      }
    });
  }

  onMainImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];
    this.formData.image = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result;
    };
    reader.readAsDataURL(file);
  }

  onExtraImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const files = Array.from(input.files);
    this.formData.images = files;
    this.extraImagePreviews = [];

    files.forEach((file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        this.extraImagePreviews.push(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  submitForm(): void {
    if (!this.formData.fake_user_id || !this.formData.title?.trim() || !this.formData.body?.trim()) {
      this.showAlert('MENU.DASHBOARD.ERROR', 'MENU.DASHBOARD.FILL_REQUIRED_FIELDS', 'error');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('fake_user_id', String(this.formData.fake_user_id));
    formDataToSend.append('title', this.formData.title.trim());
    formDataToSend.append('body', this.formData.body.trim());

    if (this.formData.image) {
      formDataToSend.append('image', this.formData.image);
    }

    (this.formData.images || []).forEach((file: File) => {
      formDataToSend.append('images[]', file);
    });

    this.isSubmitting = true;
    this.loading.show();

    this.apisService.createArticle(formDataToSend).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.loading.hide();
        this.translate.get([
          'MENU.DASHBOARD.SUCCESS',
          'MENU.DASHBOARD.ARTICLE_SAVED',
          'MENU.DASHBOARD.OK'
        ]).subscribe(trans => {
          Swal.fire({
            title: trans['MENU.DASHBOARD.SUCCESS'],
            text: trans['MENU.DASHBOARD.ARTICLE_SAVED'],
            icon: 'success',
            confirmButtonText: trans['MENU.DASHBOARD.OK']
          }).then(() => {
            this.router.navigate(['/articles']);
          });
        });
      },
      error: () => {
        this.isSubmitting = false;
        this.loading.hide();
        this.showAlert('MENU.DASHBOARD.ERROR', 'MENU.DASHBOARD.FIX_ERRORS', 'error');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/articles']);
  }

  private showAlert(titleKey: string, textKey: string, icon: 'success' | 'error' | 'warning'): void {
    this.translate.get([titleKey, textKey, 'MENU.DASHBOARD.OK']).subscribe(trans => {
      Swal.fire({
        title: trans[titleKey],
        text: trans[textKey],
        icon,
        confirmButtonText: trans['MENU.DASHBOARD.OK']
      });
    });
  }
}
